import isArray from 'lodash/isArray';

import { get as getConstants } from '@lumapps/constants';
import { Route, createPageRoute } from '@lumapps/router';
import { AppId } from '@lumapps/router/constants';
import { getArrayFacetValue } from '@lumapps/router/utils';
import { webviewSearchRoute } from '@lumapps/search-webview/routes';

import { FACET_PREFIX, SEARCH_GROUPS_RESTRICTIONS_PARAM, getPrefixedFacet, SEARCH_PAGE_PARAM } from './constants';
import { FacetOptions } from './types';
import { shouldUseGroupsRestrictions } from './utils/shouldUseGroupsRestrictions';

const searchRoute = createPageRoute({
    legacyId: 'app.front.search',
    slug: 'search/:query/:filter?',
    appId: AppId.frontOffice,
});

const mobileSearchRoute = createPageRoute({
    slug: 'mobile/search',
    appId: AppId.frontOffice,
});

const isSearchUrl = (slug: string) => {
    return slug.indexOf('/search/') === 0 || slug.indexOf(mobileSearchRoute.path) === 0;
};

const getSearchQueryParam = (facets?: FacetOptions[], sort?: string, page?: number, agent?: string) => {
    const queryParams: Record<string, string> = {};

    if (facets) {
        facets.forEach((facet: FacetOptions) => {
            const facetValue = isArray(facet.value) ? getArrayFacetValue(facet.value) : facet.value;

            Object.assign(queryParams, {
                [facet.id.indexOf(FACET_PREFIX) >= 0 ? facet.id : getPrefixedFacet(facet.id)]: facetValue,
            });
        });
    }

    if (sort) {
        queryParams.sort = sort;
    }

    // forward the groups restrictions param
    if (shouldUseGroupsRestrictions()) {
        queryParams[SEARCH_GROUPS_RESTRICTIONS_PARAM] = 'true';
    }

    if (page) {
        queryParams[SEARCH_PAGE_PARAM] = `${page}`;
    }

    if (agent) {
        queryParams.agent = agent;
    }

    return queryParams;
};

/**
 * Main search page entry point for the search page.
 * Automatically uses the correct route based on the current application context.
 * @param query string to be used for searching
 * @param filter the filter that will be shown upon redirection. By default is 'all'
 */
const search = (
    query: string,
    filter?: string,
    facets?: FacetOptions[],
    sort?: string,
    page?: number,
    agent?: string,
): Route => {
    const { applicationId } = getConstants();

    const params = {
        query,
        /**
         * Using `filter || undefined` here instead of letting
         * js setting undefined by default will remove the trailing
         * slash in the url if filter is not set.
         */
        filter: filter || undefined,
    };

    const queryParams = getSearchQueryParam(facets, sort, page, agent);

    // Use webview route when in webview app context, otherwise use standard search route
    const baseRoute = applicationId === AppId.webview ? webviewSearchRoute : searchRoute;

    const mobilePageDescriptor: Route['mobilePageDescriptor'] = { type: 'web-internal' };

    return {
        ...baseRoute,
        params,
        query: queryParams,
        mobilePageDescriptor,
    };
};

export { search, isSearchUrl, searchRoute, mobileSearchRoute, webviewSearchRoute };
